import { useEffect } from "react";
import { crumb, getYachtId } from "src/utils";

const QUEUE_KEY = "pendingUpdateCheckAnalytics";

type UpdateCheckAnalyticsPayload = {
  status: "failed" | "blocked" | "offline" | "unsupported";
  reason: string;
  error?: string;
  platform: string;
  appVersion: string;
  online: boolean;
  timestamp: number;
};

const readQueue = (): UpdateCheckAnalyticsPayload[] => {
  try {
    const raw = sessionStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeQueue = (items: UpdateCheckAnalyticsPayload[]) => {
  if (items.length === 0) {
    sessionStorage.removeItem(QUEUE_KEY);
    return;
  }
  sessionStorage.setItem(QUEUE_KEY, JSON.stringify(items));
};

/**
 * Forwards Electron GitHub update-check failures/blocks via crumb().
 * Events before yacht id is available are queued and flushed once it exists.
 */
export const useReportUpdateCheckAnalytics = () => {
  useEffect(() => {
    if (!window.api?.onUpdateCheckAnalytics) return;

    const send = (payload: UpdateCheckAnalyticsPayload) => {
      if (!getYachtId()) {
        writeQueue([...readQueue(), payload]);
        return;
      }
      crumb("Desktop auto-update check", payload, { allYachts: true });
    };

    const flush = () => {
      if (!getYachtId()) return;
      const queued = readQueue();
      if (queued.length === 0) return;
      writeQueue([]);
      queued.forEach(send);
    };

    window.api.onUpdateCheckAnalytics(payload => {
      send(payload);
    });

    flush();
    const interval = window.setInterval(flush, 30_000);

    return () => {
      window.clearInterval(interval);
      window.api?.onUpdateCheckAnalytics(() => {});
    };
  }, []);
};
